import { Icon } from 'ui';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChatButton from '../ChatButton';
import ChatLink from '../ChatLink';
import './ChatContentReferences.scss';
import getReferencePage from './getReferencePage';

const REFERENCE_TYPES = {
  WEB_REFERENCE: 'WEB_REFERENCE',
  CONTENT_REFERENCE: 'CONTENT_REFERENCE',
};

const CONTENT_TYPES = {
  MINT: 'MINT',
  MINT_PAGE: 'MINT_PAGE',
  MINT_PAGE_DUAL: 'MINT_PAGE_DUAL',
};

const SOURCE_TYPES = {
  WEB: 'web',
  COURSE: 'course',
};

function ChatContentReferences({ onSourceClick, references = [] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation(['chatbot']);

  if (!references || references.length === 0) {
    return null;
  }

  const hasMultipleReferences = references.length > 1;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const buildDeepLink = (reference) => {
    // Handle web references
    if (reference.type === REFERENCE_TYPES.WEB_REFERENCE && reference.url) {
      return reference.url;
    }

    // Handle content references
    if (reference.contentType === CONTENT_TYPES.MINT) {
      return `/course/${reference.course.id}/unit/${reference.unit.id}/lesson/${reference.lesson.id}`;
    } else if (reference.contentType === CONTENT_TYPES.MINT_PAGE || reference.contentType === CONTENT_TYPES.MINT_PAGE_DUAL) {
      const moduleOrSubmoduleId = reference.lesson?.id || reference.unit?.id;
      return `/course/${reference.course.id}/lesson/${moduleOrSubmoduleId}`;
    }
    return `/viewerfull/${reference.contentId}`;
  };

  const getReferenceTitle = (reference) => {
    if (reference.type === REFERENCE_TYPES.WEB_REFERENCE) {
      return reference.title || t('chatbot:Web');
    }
    return reference.lesson?.title || reference.unit?.title || reference.course?.title || t('chatbot:Course');
  };

  const isWebReference = (reference) => {
    return reference.type === REFERENCE_TYPES.WEB_REFERENCE;
  };

  const buildSectionDisplayReferences = (sectionReferences) => {
    const titleCounts = {};
    const titleIndexes = {};

    sectionReferences.forEach((reference) => {
      const baseTitle = getReferenceTitle(reference);
      titleCounts[baseTitle] = (titleCounts[baseTitle] || 0) + 1;
    });

    return sectionReferences.map((reference) => {
      const baseTitle = getReferenceTitle(reference);
      titleIndexes[baseTitle] = (titleIndexes[baseTitle] || 0) + 1;

      const suffix = titleCounts[baseTitle] > 1 ? ` - (${titleIndexes[baseTitle]})` : '';

      return {
        baseTitle,
        reference,
        suffix,
      };
    });
  };

  const courseReferences = buildSectionDisplayReferences(references.filter((ref) => !isWebReference(ref)));
  const webReferences = buildSectionDisplayReferences(references.filter((ref) => isWebReference(ref)));

  const hasBothTypes = webReferences.length > 0 && courseReferences.length > 0;

  // Kept outside the link: the link does not open that page, and its accessible name must not change
  const renderReferencePage = (reference) => {
    const page = getReferencePage(reference);
    if (page === null) return null;

    return (
      <>
        <span className="chat-content-references__page" aria-hidden="true">
          {t('chatbot:source_page_short', { page })}
        </span>
        <span className="sr-only">{t('chatbot:source_page', { page })}</span>
      </>
    );
  };

  // The index makes the key unique when two sources share a contentId; the list is never reordered
  const renderReferenceRow = (reference, index, title) => (
    <div key={`${reference.contentId || reference.url || 'reference'}-${index}`} className="chat-content-references__row">
      {title}
      {renderReferencePage(reference)}
    </div>
  );

  const renderReferenceLink = (displayReference, index) => {
    const { baseTitle, reference, suffix } = displayReference;
    const isWeb = isWebReference(reference);
    const link = buildDeepLink(reference);

    function handleReferenceClick() {
      if (onSourceClick) onSourceClick(isWeb ? SOURCE_TYPES.WEB : SOURCE_TYPES.COURSE);
    }

    const referenceLink = (
      <ChatLink href={link} external={true} className="chat-content-references__link" onClick={handleReferenceClick}>
        {isWeb ? (
          <span className="chat-content-references__link-text">{`${baseTitle}${suffix}`}</span>
        ) : (
          <span className="chat-content-references__link-text">
            <span dangerouslySetInnerHTML={{ __html: `${baseTitle}${suffix}` }} />
          </span>
        )}
      </ChatLink>
    );

    return renderReferenceRow(reference, index, referenceLink);
  };

  const renderExtendedReferences = (displayReference, index) => {
    const { baseTitle, reference, suffix } = displayReference;
    const referenceTitle = (
      <span className="chat-content-references__link">
        <span className="chat-content-references__link-text" dangerouslySetInnerHTML={{ __html: `${baseTitle}${suffix}` }} />
        {reference.course && (
          <>
            {' '}
            - <span className="chat-content-references__link-text-course-title" dangerouslySetInnerHTML={{ __html: `${reference.course.title || ''}` }} />
          </>
        )}
      </span>
    );

    return renderReferenceRow(reference, index, referenceTitle);
  };

  return (
    <div className={`chat-content-references ${!hasMultipleReferences || isExpanded ? 'chat-content-references--expanded' : ''}`}>
      <div className="chat-content-references__header">
        <span className="chat-content-references__title">{t('chatbot:Source')}</span>
        {hasMultipleReferences && (
          <ChatButton onClick={toggleExpanded} type="button" icon={isExpanded ? 'circle-less' : 'icon-plus'} iconPosition="right" variant="text">
            {isExpanded ? t('chatbot:Show less') : t('chatbot:Show')}
          </ChatButton>
        )}
      </div>
      <div className={`chat-content-references__list ${hasMultipleReferences && !isExpanded ? 'chat-content-references__list--hidden' : ''}`}>
        {/* Course References Section */}
        {courseReferences.length > 0 && (
          <div className="chat-content-references__section">
            <div className="chat-content-references__section-header">
              <Icon name="school" className="chat-content-references__section-icon" color="black" size="sm" />
              <span className="chat-content-references__section-title">{t('chatbot:Course')}:</span>
            </div>
            <div className="chat-content-references__section-links">
              {courseReferences.map((reference, index) =>
                reference.reference?.isExtended ? renderExtendedReferences(reference, index) : renderReferenceLink(reference, index)
              )}
            </div>
          </div>
        )}

        {/* Separator between sections */}
        {hasBothTypes && <div className="chat-content-references__separator" />}

        {/* Web References Section */}
        {webReferences.length > 0 && (
          <div className="chat-content-references__section">
            <div className="chat-content-references__section-header">
              <Icon name="weblink" className="chat-content-references__section-icon" color="black" size="sm" />
              <span className="chat-content-references__section-title">{t('chatbot:Web')}:</span>
            </div>
            <div className="chat-content-references__section-links">{webReferences.map((reference, index) => renderReferenceLink(reference, index))}</div>
          </div>
        )}
      </div>
    </div>
  );
}

ChatContentReferences.propTypes = {
  onSourceClick: PropTypes.func,
  references: PropTypes.arrayOf(
    PropTypes.shape({
      // Common fields
      type: PropTypes.oneOf([REFERENCE_TYPES.CONTENT_REFERENCE, REFERENCE_TYPES.WEB_REFERENCE]),
      title: PropTypes.string,

      // Content reference fields
      contentId: PropTypes.string,
      contentType: PropTypes.string,
      course: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
      }),
      unit: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
      }),
      lesson: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
      }),

      // Web reference fields
      url: PropTypes.string,
      snippet: PropTypes.string,
      domain: PropTypes.string,
      metadata: PropTypes.shape({
        page: PropTypes.number,
        position: PropTypes.number,
        publishDate: PropTypes.string,
        author: PropTypes.string,
        score: PropTypes.number,
      }),
    })
  ).isRequired,
};

export default ChatContentReferences;
